import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, Dimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import Footer from '../components/Footer';
import { LineChart } from 'react-native-chart-kit';
import { formatCurrency } from '../utils/formatters';

const CAGRCalculatorScreen = () => {
  const navigation = useNavigation();
  const [screenData, setScreenData] = useState(Dimensions.get('window'));
  const isMobile = screenData.width < 900;

  useEffect(() => {
    const subscription = Dimensions.addEventListener('change', ({ window }) => {
      setScreenData(window);
    });
    return () => subscription?.remove();
  }, []);

  const [results, setResults] = useState(null);
  const [showFAQ, setShowFAQ] = useState(true);

  const [formData, setFormData] = useState({
    startValue: '100000',
    endValue: '250000',
    years: '5',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateCAGR = () => {
    const S = parseFloat(formData.startValue);
    const E = parseFloat(formData.endValue);
    const T = parseFloat(formData.years);

    if ([S, E, T].some(v => isNaN(v) || v <= 0)) { setResults(null); return; }
    const cagr = Math.pow(E / S, 1 / T) - 1;

    const yearlySeries = [];
    for (let y = 1; y <= T; y++) {
      yearlySeries.push(S * Math.pow(1 + cagr, y));
    }

    setResults({
      startValue: Math.round(S),
      endValue: Math.round(E),
      cagr,
      gain: Math.round(E - S),
      years: T,
      yearlySeries: yearlySeries.map(v => Math.round(v)),
    });
  };

  useEffect(() => { calculateCAGR(); }, [formData]);

  const renderHeader = () => (
    <View style={styles.heroSection}>
      <View style={styles.heroContent}>
        <Text style={styles.heroTitle}>CAGR Calculator</Text>
        <Text style={styles.heroSubtitle}>Compute Compound Annual Growth Rate between two values</Text>
      </View>
    </View>
  );

  const renderCalculator = () => (
    <View style={styles.calculatorContainer}>
      <View style={[styles.calculatorRow, isMobile && { flexDirection: 'column' }]}>
        <View style={[styles.formContent, isMobile && { flex: 1, width: '100%' }]}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Starting Value</Text>
            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>₹</Text>
              <TextInput style={styles.input} value={formData.startValue} onChangeText={(v)=>handleInputChange('startValue',v)} keyboardType="numeric" placeholder="100000" />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Ending Value</Text>
            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>₹</Text>
              <TextInput style={styles.input} value={formData.endValue} onChangeText={(v)=>handleInputChange('endValue',v)} keyboardType="numeric" placeholder="250000" />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Years</Text>
            <View style={styles.inputContainer}>
              <TextInput style={styles.input} value={formData.years} onChangeText={(v)=>handleInputChange('years',v)} keyboardType="numeric" placeholder="5" />
              <Text style={styles.suffixText}>Years</Text>
            </View>
          </View>
        </View>

        <View style={[styles.summaryPanel, isMobile && { flex: 1, width: '100%', marginTop: 16 }]}>
          <View style={styles.panelHeader}>
            <Text style={styles.panelTitle}>Growth Summary</Text>
            <Text style={styles.panelSubtitle}>Compound Annual Growth Rate</Text>
          </View>
          {results ? (
            <View style={styles.resultsContainer}>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>CAGR</Text><Text style={styles.summaryValue}>{(results.cagr * 100).toFixed(2)}%</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Starting Value</Text><Text style={styles.summaryValue}>{formatCurrency(results.startValue)}</Text></View>
              <View style={styles.summaryRow}><Text style={styles.summaryLabel}>Ending Value</Text><Text style={styles.summaryValue}>{formatCurrency(results.endValue)}</Text></View>
              <View style={styles.totalRow}><Text style={styles.totalLabel}>Total Gain</Text><Text style={styles.totalValue}>{formatCurrency(results.gain)}</Text></View>
              {renderChart()}
            </View>
          ) : (
            <Text style={styles.placeholderText}>Enter valid inputs to view results</Text>
          )}
        </View>
      </View>
    </View>
  );

  const renderChart = () => {
    if (!results?.yearlySeries?.length) return null;
    const labels = results.yearlySeries.map((_, i) => {
      const y = i + 1; return (y === 1 || y === results.years) ? String(y) : '';
    });
    const chartWidth = isMobile ? Math.min(screenData.width - 40, 320) : 320;
    return (
      <View style={styles.chartSection}>
        <Text style={styles.chartTitle}>Yearly Projection</Text>
        <LineChart
          data={{ labels, datasets: [{ data: results.yearlySeries }] }}
          width={chartWidth}
          height={180}
          yAxisLabel="₹"
          chartConfig={{
            backgroundColor: '#ffffff',
            backgroundGradientFrom: '#ffffff',
            backgroundGradientTo: '#ffffff',
            decimalPlaces: 0,
            color: () => '#3B82F6',
            labelColor: () => '#0f172a',
          }}
          style={{ marginTop: 8 }}
        />
      </View>
    );
  };

  const renderInfo = () => (
    <View style={styles.infoSection}>
      <Text style={styles.sectionTitle}>Formula used</Text>
      <Text style={styles.sectionText}>CAGR = (Ending Value / Starting Value)^(1/Years) − 1. It smooths growth to an annual rate, ignoring volatility in between.</Text>
    </View>
  );

  const renderFAQ = () => (
    <View style={styles.faqContainer}>
      <TouchableOpacity style={styles.faqHeader} onPress={() => setShowFAQ(!showFAQ)}>
        <Text style={styles.faqTitle}>Frequently Asked Questions</Text>
        <Ionicons name={showFAQ ? 'chevron-up' : 'chevron-down'} size={20} color="#1f2937" />
      </TouchableOpacity>
      {showFAQ && (
        <View style={styles.faqContent}>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Is CAGR the same as average annual return?</Text>
            <Text style={styles.faqAnswer}>No. CAGR is a geometric average and better reflects compounded growth than arithmetic averages.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Can CAGR be negative?</Text>
            <Text style={styles.faqAnswer}>Yes, if ending value is lower than starting value over the period.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>How to compare investments using CAGR?</Text>
            <Text style={styles.faqAnswer}>Use CAGR to compare annualised returns across different instruments and timeframes.</Text>
          </View>
          <View style={styles.faqItem}>
            <Text style={styles.faqQuestion}>Does CAGR consider cash inflows/outflows?</Text>
            <Text style={styles.faqAnswer}>No. It only considers start and end values. Use XIRR for irregular cash flows.</Text>
          </View>
        </View>
      )}
    </View>
  );

  const renderSEO = () => (
    <View style={styles.seoContainer}>
      <Text style={styles.seoTitle}>CAGR Calculator: Annualised Growth Rate</Text>
      <Text style={styles.sectionText}>CAGR (Compound Annual Growth Rate) expresses the average annual growth of an investment over a period, assuming compounding. Enter starting value, ending value and years to compute your annualised return.</Text>

      <Text style={styles.seoSubtitle}>Related calculators</Text>
      <View style={styles.linksRow}>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('MutualFundReturnsCalculator')}>
          <Text style={styles.linkText}>Mutual Fund Returns Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('SIPCalculator')}>
          <Text style={styles.linkText}>SIP Calculator</Text>
        </TouchableOpacity>
        <TouchableOpacity style={styles.linkButton} onPress={() => navigation.navigate('CompoundInterestCalculator')}>
          <Text style={styles.linkText}>Compound Interest Calculator</Text>
        </TouchableOpacity>
      </View>
      <Text style={styles.disclaimer}>Calculated CAGR is based on entered values and assumes annual compounding.</Text>
    </View>
  );

  return (
    <ScrollView style={styles.container}>
      {renderHeader()}
      {renderCalculator()}
      {renderInfo()}
      {renderFAQ()}
      {renderSEO()}
      <Footer />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#f8fafc' },
  heroSection: { backgroundColor: '#4F46E5', paddingVertical: 24, paddingHorizontal: 30 },
  heroContent: { width: '100%', alignSelf: 'stretch' },
  heroTitle: { fontSize: 28, fontWeight: 'bold', color: '#ffffff', marginBottom: 6 },
  heroSubtitle: { fontSize: 14, color: '#e0e7ff' },
  calculatorContainer: { width: '100%', alignSelf: 'stretch', paddingHorizontal: 30, paddingVertical: 20 },
  calculatorRow: { flexDirection: 'row', gap: 24 },
  formContent: { gap: 20, flex: 0.7 },
  summaryPanel: { flex: 0.3, backgroundColor: '#ffffff', borderRadius: 12, padding: 20, height: 'fit-content', borderWidth: 1, borderColor: '#e5e7eb' },
  panelHeader: { alignItems: 'center', marginBottom: 16, paddingBottom: 10, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  panelTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  panelSubtitle: { fontSize: 12, color: '#64748b' },
  inputGroup: { marginBottom: 16 },
  inputRow: { flexDirection: 'row', gap: 16 },
  half: { flex: 1 },
  label: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 8 },
  helperText: { fontSize: 12, color: '#64748b', marginTop: 6 },
  inputContainer: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#f8fafc', borderRadius: 8, borderWidth: 1, borderColor: '#e5e7eb', paddingHorizontal: 12, height: 48 },
  currencySymbol: { fontSize: 16, color: '#64748b', marginRight: 8 },
  percentSymbol: { fontSize: 16, color: '#64748b', marginLeft: 8 },
  suffixText: { fontSize: 14, color: '#64748b', marginLeft: 8 },
  input: { flex: 1, fontSize: 16, color: '#0f172a', borderWidth: 0 },
  resultsContainer: { gap: 12 },
  summaryRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  summaryLabel: { fontSize: 14, color: '#334155' },
  summaryValue: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingTop: 8 },
  totalLabel: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  totalValue: { fontSize: 18, fontWeight: 'bold', color: '#065f46' },
  placeholderText: { fontSize: 14, color: '#64748b' },
  infoSection: { marginVertical: 20, marginHorizontal:20, backgroundColor: '#ffffff', padding: 20, borderRadius: 12, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4 },
  sectionTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a', marginBottom: 12 },
  sectionText: { fontSize: 16, color: '#475569', lineHeight: 24, marginBottom: 12 },
  faqContainer: { marginBottom: 20, marginHorizontal:20 },
  faqHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 20, backgroundColor: '#4F46E510', borderRadius: 12 },
  faqTitle: { fontSize: 20, fontWeight: 'bold', color: '#0f172a' },
  faqContent: { backgroundColor: '#ffffff', padding: 20, borderRadius: 12, marginTop: 12, borderWidth: 1, borderColor: '#e5e7eb' },
  faqItem: { marginBottom: 16 },
  faqQuestion: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 6 },
  faqAnswer: { fontSize: 15, color: '#475569', lineHeight: 22 },
  chartSection: { marginTop: 12, alignItems: 'center' },
  chartTitle: { fontSize: 14, fontWeight: '600', color: '#0f172a', marginBottom: 8 },
  seoContainer: { backgroundColor: '#ffffff', padding: 20, marginHorizontal:20, borderRadius: 12, elevation: 2, shadowColor: '#000', shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.1, shadowRadius: 4, marginBottom: 20 },
  seoTitle: { fontSize: 22, fontWeight: 'bold', color: '#0f172a', marginBottom: 12, textAlign: 'center' },
  linksRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  linkButton: { paddingVertical: 8, paddingHorizontal: 12, backgroundColor: '#4F46E510', borderRadius: 8, borderWidth: 1, borderColor: '#e5e7eb' },
  linkText: { color: '#4F46E5', fontWeight: '600' },
  disclaimer: { fontSize: 12, color: '#64748b', marginTop: 4 },
  seoSubtitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginTop: 8, marginBottom: 6 },
});

export default CAGRCalculatorScreen;